import React, { memo, useCallback, useMemo, useRef, useState, ReactElement, MouseEvent } from "react";
import { DataTableCallbackRefs, GridMachineContext, QueueContext, QueueEdit, QueueInsert, DataTableOperations, QueueDelete, RowNetworkStatus } from "@iventis/data-table";
import { css, Theme } from "@emotion/react";
import { DoneInvokeEvent } from "xstate";
import { RelativeEventRule } from "@iventis/domain-model/model/relativeEventRule";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Content } from "@iventis/translations/content/typed-content";
import { Body1, Header4 } from "@iventis/styles/src/components/texts";
import { API_KEY_QUERY_PARAM_KEY, addTimeSpanToDate, getContrastTextColour, hasObjectArrayChanged, queryParams } from "@iventis/utilities";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { InsertRowBelowComponent } from "@iventis/data-table/components/insert-row-below";
import { InsertRowAboveComponent } from "@iventis/data-table/components/insert-row-above";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { toast } from "@iventis/toasts/src/toast";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { greyTheme } from "@iventis/styles";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FormWizardTemplate, ConfirmationDialogComponent, CustomDialog } from "@iventis/components";
import { RequestAggregator } from "@iventis/api-helpers/src/request-aggregator";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { scheduleApi } from "@iventis/api/src/api";
import { TimeSpan } from "@iventis/types";
import { Menu, MenuItem, useTheme } from "@mui/material";
import { toastError } from "@iventis/data-table/helpers/data-table-toasts";
import { DataTable } from "@iventis/data-table/components/data-table";
import { ScheduleMachineContext, GetEventBlockHierarchy } from "./schedule.machine.types";
import {
    calculateRowIndex,
    createNewActivity,
    generateColumnsForRunSheet,
    generateRowsForRunSheet,
    rowFromEventBlock,
    hasATimeRule,
    shouldRunSheetRowBeIndexed,
    isZeroRow,
    hasRunSheetValueChanged,
    eventBlockFromRow,
    findClosestSession,
    columnsBackendIgnores,
} from "./run-sheet-helpers";
import { RunSheetMenu } from "./run-sheet-menu";
import { InsertRowPosition, RunSheetData, RunSheetRowNode } from "./run-sheet.types";
import { createEventBlock, deleteEventBlock, exportRunSheetToExcel, getEventBlockSingleHierarchy, updateEventBlock, updateEventBlockDone } from "./schedule-api-functions";
import { findDirectEventBlockDescendantsBySpecificRelativeEventRuleRelationship, hasRelativeRuleChanged, relativeEventRuleKeysRequiringRefresh } from "./time-rules.helpers";
import { findHierarchyEntityById, findHierarchyEntityByType } from "./hierarchy.helpers";
import { DaysToExportType, ExportRunSheetFormComponent } from "./export-run-sheet-form";

type UpdateEventBlockHierarchyFromRunSheet = (params: GetEventBlockHierarchy) => void;

enum RunSheetDialog {
    DeleteSaved = "DeleteSaved",
    DeleteUnsaved = "DeleteUnsaved",
    Export = "Export",
}

type InsertRowResponsePayload = { eventBlock: Hierarchy<EventBlock>; rowData: RunSheetData };

// Memoize the entire component as once this is rendered, we don't want to re-render.
// This is because when we make edits to the data table, we send the results to the machine.
// The machine then updates it's context triggering a re-rendering (causing a flickering effect).
// The data table however has it's own local state with the updated values already, so doesn't require this re-render
export const RunSheet: React.FC<{
    machineContext: ScheduleMachineContext;
    updateEventBlockHierarchy: UpdateEventBlockHierarchyFromRunSheet;
    loadingBar: ReactElement;
    timezone: string;
    permission: Authorisation;
}> = memo(({ machineContext, updateEventBlockHierarchy, loadingBar, timezone, permission }) => {
    const translate = useIventisTranslate();
    const ref = useRef<DataTableCallbackRefs<RunSheetData>>();

    // state for inserting the event block
    const [insertEventBlockMenuAnchor, setInsertEventBlockMenuAnchor] = useState<null | HTMLElement>(null);

    const [insertEventBlockPosition, setInsertEventBlockPosition] = useState<null | InsertRowPosition>(null);

    const theme = useTheme<Theme>();

    /** Refetches the hierarchy from the back end (will re-load the component) */
    const refetchHierarchy = () => updateEventBlockHierarchy({ type: "GET_EVENT_BLOCK_HIERARCHY" });

    const [editingATimeRule, setEditingATimeRule] = useState(false);

    const [invalidRowCount, setInvalidRowCount] = useState(0);

    /** If we fail to update, refresh the table so the local rows are in sync with our eventBlockHierarchy */
    const cleanUpFailedRequest = () => {
        setEditingATimeRule(false);
        refetchHierarchy();
    };

    const runSheetVenueLocation = useMemo(() => machineContext.selectedVenueLocations[0], []);
    const dcasDataFieldIds = useMemo(() => {
        const { dataFields } = machineContext.schedule.config.dcasConfig;
        const orderedDatafields = dataFields.sort((a, b) => a.index - b.index);
        return orderedDatafields.map(({ dataFieldId }) => dataFieldId);
    }, []);

    const dcasDataFields = useMemo(() => dcasDataFieldIds.map((dataFieldId) => machineContext.schedule.config.dataFields.find((df) => df.id === dataFieldId)), [dcasDataFieldIds]);

    const listItemRequestAggregator = new RequestAggregator<DataFieldListItem[], string>(scheduleApi, (id) => `/data-field-list-items/${id}`);

    const hasPermission = permission === Authorisation.Admin || permission === Authorisation.Write;

    const columns = useMemo(
        () =>
            generateColumnsForRunSheet(
                machineContext,
                dcasDataFields,
                runSheetVenueLocation,
                listItemRequestAggregator.get.bind(listItemRequestAggregator) as typeof listItemRequestAggregator.get,
                hasPermission
            ),
        []
    );

    const rows = useMemo(() => generateRowsForRunSheet(machineContext, runSheetVenueLocation, timezone, hasPermission), [machineContext]);

    const [selectedRow, setSelectedRow] = useState<RunSheetRowNode>(null);

    const [runSheetDialog, setRunSheetDialog] = useState<RunSheetDialog | null>(null);

    const deleteDisabled = selectedRow == null || !selectedRow.data.type.dcasCreationAllowed; // If you can't create it, you shouldn't be able to delete it

    const deleteRowRequest = () => {
        if (selectedRow.data.sessionId == null) {
            // If no session id, there won't be any descendants, so we can just delete it
            ref.current?.deleteRequest();
        } else {
            // If session id, check for descendants
            const session = findHierarchyEntityById(machineContext.eventBlockHierarchy.children, selectedRow.data.sessionId);
            const related = findDirectEventBlockDescendantsBySpecificRelativeEventRuleRelationship(session, selectedRow.data.id);
            if (related.length > 0) {
                toast.info({ title: translate(Content.common.alert), message: translate(Content.timerules.info.can_not_delete) });
            } else {
                ref.current?.deleteRequest();
            }
        }
    };

    /** Insert row into the data table (local) */
    const insertRowIntoRunSheetTable = (typeId: string) => {
        const selectedDay = machineContext.availableDays.get(machineContext.day);
        const startAt = selectedRow?.data ? selectedRow.data.startAt.value : addTimeSpanToDate(machineContext.schedule.dayStartAt as TimeSpan, selectedDay);
        ref.current.insertRow(
            [
                createNewActivity(
                    startAt,
                    startAt, // default to 0 duration
                    machineContext.schedule.config.eventBlockTypes.find((t) => t.id === typeId),
                    selectedRow?.data ? selectedRow.data.sessionId : null,
                    timezone,
                    hasPermission
                ),
            ],
            selectedRow?.rowIndex ? calculateRowIndex(selectedRow.rowIndex, insertEventBlockPosition) : 0
        );
        setInsertEventBlockMenuAnchor(null);
        setInsertEventBlockPosition(null);
    };

    const onRunSheetMenuClicked = (event: MouseEvent<HTMLButtonElement>, placement: InsertRowPosition) => {
        setInsertEventBlockMenuAnchor(event.currentTarget);
        setInsertEventBlockPosition(placement);
    };

    /** Using the newly created row, post an event block to the backend  */
    const createEventBlockFromRowAsync = useCallback(async (context: GridMachineContext<RunSheetData>) => {
        const { node } = context.queue[0] as QueueInsert;
        const data = node.data as RunSheetData;
        const getScheduleVersionId = () => {
            switch (data.type?.calendarLevel) {
                case 2: {
                    const session = findHierarchyEntityById(machineContext.eventBlockHierarchy.children, data.sessionId);
                    return session?.item?.scheduleVersionId;
                }
                default:
                    return machineContext.selectedScheduleVersionRootId;
            }
        };
        const eventBlock: Hierarchy<EventBlock> = eventBlockFromRow(
            data,
            machineContext.schedule.config.dataFields,
            machineContext.selectedVenueLocations[0].item,
            getScheduleVersionId()
        );
        let requiresRefresh = false;

        if (data.relativeEventRules?.length) {
            setEditingATimeRule(true);
            requiresRefresh = true;
        }
        const eventBlockResponse = await createEventBlock(eventBlock);
        if (requiresRefresh) {
            refetchHierarchy();
        }
        return { eventBlock: eventBlockResponse, rowData: data };
    }, []);

    /** Delete event block on the backend */
    const deleteEventBlockFromRunSheetAsync = async (context: GridMachineContext<RunSheetData>) => {
        const row = (context.queue[0] as QueueDelete<RunSheetData>)?.row;
        const { id, type } = row;
        await deleteEventBlock(id, type);
        return row;
    };

    /** Update the edited event block on the backend (no need for a return as the table is up to date anyway)  */
    const updateEventBlockFromRowAsync = useCallback(
        async (context: GridMachineContext<RunSheetData>): Promise<undefined> => {
            const { node, update } = context.queue[0] as QueueEdit<RunSheetData>;
            const [columnId, value] = update; // Currently only support one update at a time
            if (columnId === "done") {
                await updateEventBlockDone(
                    {
                        eventBlockId: node.data.id,
                        relativeEventBlockId: node.data.linkedEventId ?? node.data.sessionId,
                        doneDate: machineContext.availableDays.get(machineContext.day),
                        done: value,
                    },
                    node.data.type
                );
                return;
            }
            const { data } = node;
            // If a time rule is present, the back end doesn't care for it's startAt, endAt or duration values
            if (isZeroRow(data) || (hasATimeRule(data) && ["startAt", "endAt", "duration"].includes(columnId)) || columnsBackendIgnores.includes(columnId)) {
                return;
            }
            // The eventBlock to update
            const eventBlock = await getEventBlockSingleHierarchy(data.id);
            const oldEventBlockItem = JSON.parse(JSON.stringify(eventBlock.item));

            let requiresRefresh = false;

            const { dataFields } = machineContext.schedule.config;
            switch (columnId) {
                case "startAt": {
                    eventBlock.item[columnId] = value.value;
                    break;
                }
                case "endAt": {
                    eventBlock.item[columnId] = value.value;
                    break;
                }
                case "estimatedTime": {
                    eventBlock.item.estimatedTime = value;
                    break;
                }
                case "relativeEventRules": {
                    // Check the condition of the change to see if we should be refreshing the table or not
                    const keysOfChangedValues: (keyof RelativeEventRule)[] = [];
                    hasObjectArrayChanged(eventBlock.item.relativeEventRules, data.relativeEventRules, hasRelativeRuleChanged, keysOfChangedValues);

                    if (!(value?.length > 0)) {
                        if (node.data.startAt.value == null) {
                            throw new Error("Since we are removing a relative rule, the event block must have a startAt value!");
                        }
                        // Reaches here if event block has switched from relative rule to fixed time. Thus, we have to send down the start/end times, as previously they would be null in the db
                        eventBlock.item.startAt = node.data.startAt.value;
                        eventBlock.item.endAt = node.data.endAt.value;
                    }

                    // Only requires a refresh if the relativeEventRuleKeysRequiringRefresh have changed
                    requiresRefresh = keysOfChangedValues.some((key) => relativeEventRuleKeysRequiringRefresh.includes(key)) || !(eventBlock.item.relativeEventRules?.length > 0);

                    if (requiresRefresh) {
                        setEditingATimeRule(true);
                    }

                    eventBlock.item[columnId] = data.relativeEventRules;

                    break;
                }
                case "activity": {
                    if (typeof value === "string" && data.type.calendarLevel == null) {
                        // Activity here refers to an activities name
                        eventBlock.item.name = value;
                    }
                    break;
                }
                case "venueLocation": {
                    if (value == null && data.type.calendarLevel == null) {
                        // If the activity's venue is null, we must still tie it to the venue for filtering purposes
                        eventBlock.item[columnId] = machineContext.selectedVenueLocations[0].item;
                    }
                    eventBlock.item[columnId] = value;
                    break;
                }
                default: {
                    if (!dataFields.map(({ id }) => id).includes(columnId)) {
                        // Column not identified
                        return;
                    }
                    break;
                }
            }

            Object.entries(data).forEach(([dataFieldId, value]) => {
                const dataField = dataFields.find(({ id }) => dataFieldId === id);
                if (dataField) {
                    // Loop through the data fields and add, update or remove the values based on its' previous existence
                    const dfv = eventBlock.item.dataFieldValues.find((dfv) => dfv.dataField.id === dataFieldId);
                    if (dfv == null) {
                        eventBlock.item.dataFieldValues.push({ dataField: dataFields.find((df) => df.id === dataFieldId), value });
                    } else if (value != null) {
                        dfv.value = value;
                    } else {
                        eventBlock.item.dataFieldValues = eventBlock.item.dataFieldValues.filter(({ dataField }) => dataField.id !== dataFieldId);
                    }
                    if (eventBlock.item.type?.calendarLevel === 2) {
                        // Currently the PUT route requires the parent id to be the event units' sessions' id in order to change it's phase (parent)
                        // ToDo: This can be removed once Events and Phases have been moved from event blocks to data fields
                        eventBlock.item.parentId = data.sessionId;
                    }
                }
            });
            try {
                // Temp fix for sending down the correct type. ToDo: Remove this once bug 5323 is done. https://dev.azure.com/iventis/Iventis/_backlogs/backlog/Iventis%20Team/Stories/?workitem=5323
                eventBlock.item.type = machineContext.schedule.config.eventBlockTypes.find(({ id }) => id === data.type?.id);
                await updateEventBlock(oldEventBlockItem, eventBlock, data.sessionId, false);
                if (requiresRefresh) {
                    // Then refetch and let the back end create the hierarchy if required
                    refetchHierarchy();
                }
            } catch {
                cleanUpFailedRequest();
            }
        },
        [machineContext]
    );

    /** Side effects of a successful network request (post/put/delete)  */
    const onNetworkRequestSuccess = useCallback(
        (context: QueueContext<RunSheetData>, event: DoneInvokeEvent<InsertRowResponsePayload | string>) => {
            if (event.data == null) {
                return;
            }
            const api = ref.current?.getApi();
            switch (context.operation) {
                case DataTableOperations.Insert: {
                    const { eventBlock, rowData } = event.data as InsertRowResponsePayload;
                    const actualEventBlock = findHierarchyEntityByType([eventBlock], rowData.type?.id, (e) => e.type.id);
                    // Add to run sheet table
                    const serverData = rowFromEventBlock(
                        actualEventBlock.item,
                        dcasDataFields,
                        runSheetVenueLocation,
                        (context.node.data as RunSheetData).sessionId,
                        timezone,
                        hasPermission,
                        {
                            startAt: rowData.startAt,
                            endAt: rowData.endAt,
                            activity: rowData.activity,
                            estimatedTime: rowData.estimatedTime,
                        }
                    );
                    api?.getRowNode(context.node.id)?.setData(serverData);
                    break;
                }
                default: {
                    break;
                }
            }
        },
        [ref.current]
    );

    if (editingATimeRule) {
        return loadingBar;
    }

    return (
        <>
            <RunSheetMenu
                options={[
                    {
                        disabled: queryParams[API_KEY_QUERY_PARAM_KEY] != null, // Export relies on web sockets, api-keys are not supported by web sockets
                        visible: true,
                        onClicked: () => setRunSheetDialog(RunSheetDialog.Export),
                        component: <FontAwesomeIcon icon={["far", "file-spreadsheet"]} />,
                        altText: translate(Content.runsheet.actions.export_as_excel),
                        key: "export",
                    },
                    {
                        disabled: deleteDisabled || !hasPermission,
                        visible: true,
                        onClicked: deleteRowRequest,
                        component: <FontAwesomeIcon icon={["far", "trash"]} />,
                        altText: translate(Content.table.deleteRow),
                        key: "delete",
                    },
                    {
                        disabled: invalidRowCount > 0 || !hasPermission,
                        visible: true,
                        onClicked: (event) => onRunSheetMenuClicked(event, InsertRowPosition.BELOW),
                        component: <InsertRowBelowComponent />,
                        altText: translate(Content.runsheet.actions.insert_row_below),
                        key: "insert-below",
                        dataCy: "insert-below",
                    },
                    {
                        disabled: invalidRowCount > 0 || !hasPermission,
                        visible: true,
                        onClicked: (event) => onRunSheetMenuClicked(event, InsertRowPosition.ABOVE),
                        component: <InsertRowAboveComponent fill={theme.primaryColors.subdued70} />,
                        altText: translate(Content.runsheet.actions.insert_row_above),
                        key: "insert-above",
                        dataCy: "insert-above",
                    },
                ]}
            />
            <Menu
                open={Boolean(insertEventBlockMenuAnchor)}
                anchorEl={insertEventBlockMenuAnchor}
                onClose={() => setInsertEventBlockMenuAnchor(null)}
                onBackdropClick={() => setInsertEventBlockMenuAnchor(null)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "center",
                    horizontal: "right",
                }}
            >
                {machineContext.schedule.config.eventBlockTypes
                    .filter((type) => type.dcasCreationAllowed)
                    .map((type) => {
                        const translatableText = Content.runsheet.add[type.name];
                        const name = translatableText ? translate(translatableText) : type.name;
                        return (
                            <MenuItem
                                key={name}
                                data-cy={`add-${name}`}
                                onClick={() => insertRowIntoRunSheetTable(type?.id)}
                                disabled={type.calendarLevel != null && (selectedRow == null || selectedRow?.data.sessionId == null)}
                            >
                                {name}
                            </MenuItem>
                        );
                    })}
            </Menu>
            <DataTable<RunSheetData>
                dataTableId="run-sheet"
                ref={ref}
                gridOptions={{
                    rowData: rows,
                    headerHeight: 44,
                    rowHeight: 44,
                    suppressColumnVirtualisation: true,
                    stopEditingWhenCellsLoseFocus: false,
                    context: { translate, timezone, toast },
                    getRowClass: (params) => {
                        const { type } = params.data as RunSheetData;
                        let classNames = "";
                        switch (type?.calendarLevel) {
                            case 1: {
                                classNames += " session-row";
                                break;
                            }
                            case 2: {
                                classNames += " scheduled-item-row";
                                break;
                            }
                            default: {
                                break;
                            }
                        }
                        return classNames;
                    },
                    getRowStyle: (params) => {
                        // Apply any custom row colour from the row
                        if (params.data.rowColour) {
                            return {
                                background: params.data.rowColour,
                                color: getContrastTextColour(params.data.rowColour),
                                WebkitTextStrokeColor: getContrastTextColour(params.data.rowColour),
                            };
                        }
                        return {};
                    },
                }}
                hasValueChanged={hasRunSheetValueChanged(machineContext.schedule.config.dataFields)}
                columns={columns}
                requiredFields={["startAt", "endAt", "activity"]}
                shouldRowBeIndexed={shouldRunSheetRowBeIndexed}
                theme={greyTheme(theme, {
                    additionalCss: css`
                        .session-row {
                            border-top: 2px solid ${theme.typographyColors.subdued};
                            .index-cell {
                                background: none;
                            }
                        }
                        .session-row,
                        .scheduled-item-row {
                            font-weight: 500;
                            // If a cell isn't the "done" cell
                            .ag-cell:not(.done),
                            // Or is the done cell but it's not checked
                            .MuiCheckbox-root:not(.Mui-checked) {
                                // Use a darker colour on the svgs for sessions and schedule items
                                svg {
                                    color: ${theme.typographyColors.core};
                                }
                            }
                        }
                        .ag-row:not(.ag-row-selected) {
                            .done.ag-cell-focus {
                                border: 1px solid transparent;
                                border-right: 1px solid ${theme.typographyColors.blank};
                                background-color: transparent !important;
                            }
                        }
                    `,
                })}
                onRowSelected={(rows) => setSelectedRow(rows[0])}
                onInvalidRowCountChanged={setInvalidRowCount}
                onDeleteRequested={(rows: RunSheetData[]) => {
                    if (rows.every((row) => row.networkStatus === RowNetworkStatus.PendingCreation)) {
                        setRunSheetDialog(RunSheetDialog.DeleteUnsaved);
                    } else {
                        setRunSheetDialog(RunSheetDialog.DeleteSaved);
                    }
                }}
                onDeleteHandled={() => setRunSheetDialog(null)}
                insertRowAsync={createEventBlockFromRowAsync}
                editRowAsync={updateEventBlockFromRowAsync}
                deleteRowAsync={deleteEventBlockFromRunSheetAsync}
                onNetworkRequestSuccess={onNetworkRequestSuccess}
                onNetworkRequestError={(params) => toastError(params, refetchHierarchy)}
                defaultSort={[{ colId: "startAt", sort: "asc", sortIndex: 0 }]}
                sideEffectsOnCellValueChanged={(event) => {
                    // HANDLE EVENT BLOCK'S DONE STATE
                    const { api, data, node: currentNode, colDef, newValue } = event;
                    // If a new relative event rule value has been added, the dcas should refresh the cells anyway, meaning the backend doneness will do the work for us
                    if (colDef.field !== "relativeEventRules" || (newValue != null && newValue.length > 0)) {
                        return;
                    }
                    // When we have a rule removed, we must check if the session id (closest session) has changed from before, so we can sync the doneness with the backend
                    const sessions: RunSheetData[] = [];
                    api.forEachNode((node) => {
                        if (node.data.type.calendarLevel === 1) {
                            // collect all the top level event blocks in the dcas (sessions)
                            sessions.push(node.data);
                        }
                    });
                    // Find the closest one
                    const newSessionId = findClosestSession(data.startAt.value, sessions, (session) => session.startAt.value)?.sessionId;
                    if (newSessionId !== data.sessionId) {
                        // If they do not match, we must invalidate the event blocks' doneness (the backend)
                        api.getRowNode(currentNode.id).setDataValue("done", false);
                        api.getRowNode(currentNode.id).setDataValue("sessionId", newSessionId);
                    }
                }}
            />
            {/* Dialogs */}
            <CustomDialog open={runSheetDialog === RunSheetDialog.DeleteSaved} onClose={ref.current?.deleteCancelled}>
                <FormWizardTemplate
                    currentStage={0}
                    title={
                        <Header4 style={{ margin: "20px 22px 10px" }}>
                            <span style={{ marginRight: "8px", height: "18px" }}>
                                <FontAwesomeIcon icon={["fas", "circle-exclamation"]} />
                            </span>
                            {translate(Content.runsheet.deleting.deleting_affects_all_instances)}
                        </Header4>
                    }
                    stages={[
                        {
                            primaryButtonText: translate(Content.common.buttons.continue),
                            primaryButtonCallback: ref.current?.deleteConfirmed,
                            secondaryButtons: [{ buttonText: translate(Content.common.buttons.back), onButtonPressed: ref.current?.deleteCancelled }],
                            isValid: true,
                        },
                    ]}
                >
                    <Body1>{translate(Content.runsheet.deleting.delete_activity_description_1)}</Body1>
                    <br />
                    <Body1>{translate(Content.runsheet.deleting.delete_activity_description_2)}</Body1>
                </FormWizardTemplate>
            </CustomDialog>
            <ConfirmationDialogComponent
                show={runSheetDialog === RunSheetDialog.DeleteUnsaved}
                handleConfirm={() => ref.current.deleteConfirmed()}
                handleCancel={() => ref.current.deleteCancelled()}
                title={translate(Content.schedule2.activity.titleDeleteUnsaved)}
                message={translate(Content.schedule2.activity.descriptionDeleteUnsaved)}
                confirmText={translate(Content.common.buttons.continue)}
                cancelText={translate(Content.common.buttons.back)}
                isSubmitting={false}
            />
            <CustomDialog open={runSheetDialog === RunSheetDialog.Export} fullWidth maxWidth="sm">
                <ExportRunSheetFormComponent
                    venueName={runSheetVenueLocation.item.name}
                    onClose={() => setRunSheetDialog(null)}
                    onConfirm={(daysToExport: DaysToExportType) => {
                        toast.info({
                            title: translate(Content.common.alert),
                            message: translate(Content.common.exports.start_message, { exportName: `${runSheetVenueLocation.item.name} DCAS` }),
                        });
                        setRunSheetDialog(null);

                        exportRunSheetToExcel(
                            machineContext.selectedScheduleVersionRootId,
                            runSheetVenueLocation.item.id,
                            daysToExport === DaysToExportType.SELECTED_DAY ? machineContext.availableDays.get(machineContext.day) : null
                        );
                    }}
                    translate={translate}
                />
            </CustomDialog>
        </>
    );
});
